"use client";
import { CreatePermissionForm } from "@/features/permissions/components/CreatePermission/CreatePermissionForm";
import {
  CreatePermissionProvider,
  useCreatePermissionProvider
} from "@/features/permissions/components/CreatePermission/CreatePermissionProvider";
import { accessAssignmentGroups } from "@/features/reserve/components/resources/tabs/access-restrictions/defines";
import { AssignmentGroupType, IResource } from "@gettactic/api";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal";
import { CrudModalProps } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal.types";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";

export interface AddAssignmentModalProps
  extends Pick<CrudModalProps, "asDrawer" | "size" | "className"> {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCreated: () => void;
  resources: IResource[];
  group: AssignmentGroupType;
}

const AddAssignmentModalContent: React.FC<AddAssignmentModalProps> = (
  props
) => {
  const {
    resources,
    onCreated,
    onOpenChange,
    group,
    isOpen,
    asDrawer,
    size = "lg",
    className
  } = props;
  const queryClient = useQueryClient();
  const { resetToDefaults, form, submit, isLoading } =
    useCreatePermissionProvider();

  const users = form.watch("users", []);
  const teams = form.watch("teams", []);
  const allUsers = form.watch("allUsers", false);
  const hasSelections = users.length + teams.length + (allUsers ? 1 : 0) > 0;

  const resourceIds = resources.map((r) => r.id);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourceScheduledPermissionsAll", resourceIds]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.getResourceAdditionalAccess", resourceIds]
      })
    ]);
  };

  const handleOpenChange = async (open: boolean) => {
    if (!open) {
      await invalidateAll();
      resetToDefaults();
    }
    onOpenChange(open);
  };

  const handleSubmit = async () => {
    await form.handleSubmit(submit)();
  };

  const selectedGroup = accessAssignmentGroups[group];

  return (
    <CrudModal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title={`Add Assignments / ${selectedGroup?.label}`}
      size={size}
      asDrawer={asDrawer}
      className={className}
      onSubmit={handleSubmit}
      submitLabel="Save"
      cancelLabel="Cancel"
      submitProps={{
        disabled: !hasSelections || isLoading
      }}
      confirmWhenDirty={false}
    >
      <CreatePermissionForm isOpen={isOpen} />
    </CrudModal>
  );
};

export const AddAssignmentModal: React.FC<AddAssignmentModalProps> = (
  props
) => {
  const { resources, onCreated, group } = props;

  return (
    <CreatePermissionProvider
      key={group}
      group={group}
      resources={resources}
      onCreated={onCreated}
      onSave={() => {}}
    >
      <AddAssignmentModalContent {...props} />
    </CreatePermissionProvider>
  );
};
